import { MaybeNil } from '../../../../utils/interfaces';
import { IDateInput } from '../../../../redux/slices/oldBuilder/date/types';
import {
  DependencyList,
  EffectCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IDatePickerFieldsFragment,
  IDateRangeFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { GraphQLDate } from '../../../../utils/types';
import useComputeAbsoluteTimePeriod from './useComputeAbsoluteTimePeriod';
import { isEqual, isNil } from 'lodash';

/**
 * Produces dateInput (relative date picker + absolute start and end date)
 * *if* there is a relative date picker using either existing dateRange
 * (absolute start and end date) or computes it with a call to Kobol. In the
 * latter case calls the onChange callback in order to ensure that
 * the outer state (builder, dashboard filter) has the absolute
 * start and end dates (for example to check if date range limit is reached
 * or to limit cohortStartDate up to absolute end date
 *
 * If there is no relative date picker and the widget is open,
 * produces a defaultDateInput from defaultDatePicker to initialize the
 * widget with a default selection
 */
const calculateDateInputs = async (
  datePicker: MaybeNil<IDatePickerFieldsFragment>,
  dateRange: MaybeNil<IDateRangeFieldsFragment>,
  defaultDatePicker: IDatePickerFieldsFragment,
  endDate: GraphQLDate,
  isPopoverOpen: boolean,
  onChange: (newDateInput: IDateInput) => void,
  computeAbsoluteTimePeriod: (
    datePicker: IDatePickerFieldsFragment,
    endDate: GraphQLDate,
  ) => Promise<IDateInput>,
) => {
  let dateInput: IDateInput | null;
  if (!isNil(datePicker)) {
    if (!isNil(dateRange)) {
      dateInput = {
        datePicker,
        startDate: dateRange.start,
        endDate: dateRange.end,
      };
    } else {
      dateInput = await computeAbsoluteTimePeriod(datePicker, endDate);
      onChange(dateInput);
    }
  } else {
    dateInput = null;
  }

  let defaultDateInput: IDateInput | null = null;
  if (!isNil(dateInput)) {
    defaultDateInput = dateInput;
  } else if (isPopoverOpen) {
    defaultDateInput = await computeAbsoluteTimePeriod(
      defaultDatePicker,
      endDate,
    );
  }

  return { dateInput, defaultDateInput };
};

/**
 * Helper to trigger useDeepCompareEffect
 *
 * @param value the value to be memoized (usually a dependency list)
 * @returns a memoized version of the value as long as it remains deeply equal
 */
export const useDeepCompareMemoize = <T,>(value: T) => {
  const ref = useRef<T>(value);
  const signalRef = useRef<number>(0);

  if (!isEqual(value, ref.current)) {
    ref.current = value;
    signalRef.current += 1;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ref.current, [signalRef.current]);
};

/**
 * Similar to useEffect but performs a deep compare of the dependencies
 * Inspired by https://github.com/kentcdodds/use-deep-compare-effect
 *
 * @param callback
 * @param dependencies
 */
const useDeepCompareEffect = (
  callback: EffectCallback,
  dependencies: DependencyList,
) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useEffect(callback, useDeepCompareMemoize(dependencies));
};

type IReturn =
  | { available: false }
  | {
      available: true;
      dateInput: MaybeNil<IDateInput>;
      defaultDateInput: MaybeNil<IDateInput>;
    };

/**
 * Executes calculateDateInputs if any of the inputs change by using
 * deep compare
 *
 */
const useCalculateDateInputEffect = (
  schemaId: MaybeNil<string>,
  datePicker: MaybeNil<IDatePickerFieldsFragment>,
  dateRange: MaybeNil<IDateRangeFieldsFragment>,
  defaultDatePicker: IDatePickerFieldsFragment,
  endDate: GraphQLDate,
  isPopoverOpen: boolean,
  onChange: (newDateInput: IDateInput) => void,
): IReturn => {
  const { computeAtpLoading, computeAbsoluteTimePeriod } =
    useComputeAbsoluteTimePeriod(schemaId);

  const [returnValue, setReturnValue] = useState<{
    dateInput: MaybeNil<IDateInput>;
    defaultDateInput: MaybeNil<IDateInput>;
  } | null>(null);

  useDeepCompareEffect(() => {
    calculateDateInputs(
      datePicker,
      dateRange,
      defaultDatePicker,
      endDate,
      isPopoverOpen,
      onChange,
      computeAbsoluteTimePeriod,
    ).then(setReturnValue);
  }, [dateRange, datePicker, defaultDatePicker, endDate, isPopoverOpen]);

  if (computeAtpLoading || isNil(returnValue)) {
    return { available: false };
  } else {
    return { available: true, ...returnValue };
  }
};

export default useCalculateDateInputEffect;
